import { Box, Skeleton, Stack, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { TextInput } from "components/TextInput";
import { CommentThreadFragment, Frequency } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { DateTime } from "luxon";
import { useCallback, useEffect } from "react";
import { Controller, FormProvider, Resolver, useForm } from "react-hook-form";

import { DocumentsListControl } from "./DocumentsControl";
import { LinksControl } from "./LinksControl";
import SubtasksControl from "./SubtasksControl";
import { TaskCommentSection } from "./TaskCommentSection";
import { TaskDialogSkeleton } from "./TaskDialogSkeleton";
import { TaskPickerToolbar } from "./TaskPickerToolbar";
import { TaskTitleHeader } from "./TaskTitleHeader";
import { TaskFormType } from "./types";

export interface TaskDialogProps {
  onSubmit: (data: TaskFormType, isCompletionOnly: boolean, isFrequencyChanged: boolean) => void;
  onDeleteTask?: () => void;
  onClose: (data: TaskFormType) => void;
  initialValues: TaskFormType;
  isSubmitting: boolean;
  isLoading: boolean;
  open: boolean;
  validationResolver?: Resolver<TaskFormType>;

  // Button configuration - pushed up from callers
  cancelButtonText: string;
  /** Custom click handler for cancel button (defaults to closing dialog) */
  onCancelClick?: () => void;
  cancelButtonDisabled?: boolean;
  submitButtonText: string;
  submitButtonHelperText?: (isDirty: boolean) => string;
  /** If true, submit button is always enabled regardless of form dirty state */
  submitAlwaysEnabled?: boolean;

  // UI configuration
  /** Auto-focus the title input on open */
  autoFocusTitle?: boolean;
  /** Disable restore focus when dialog closes */
  disableRestoreFocus?: boolean;
  /** Virtual event id used for occurrence-level comments */
  virtualEventId?: string;
  /** Existing comments for this virtual event */
  comments?: CommentThreadFragment[];
  /** Show or hide the comments section */
  showCommentsSection?: boolean;
}

export const TaskDialog: React.FC<TaskDialogProps> = ({
  onSubmit,
  onDeleteTask,
  onClose,
  open,
  isSubmitting,
  initialValues,
  isLoading,
  validationResolver,
  cancelButtonText,
  onCancelClick,
  cancelButtonDisabled,
  submitButtonText,
  submitButtonHelperText,
  submitAlwaysEnabled = false,
  autoFocusTitle = false,
  disableRestoreFocus = false,
  virtualEventId,
  comments = [],
  showCommentsSection = true,
}) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const form = useForm<TaskFormType>({
    defaultValues: initialValues,
    resolver: validationResolver,
    mode: "onChange",
  });

  useEffect(() => {
    if (initialValues) {
      form.reset(initialValues);
    }
  }, [initialValues, form]);
  const { control, handleSubmit, watch } = form;

  const completedOn = watch("completedOn");
  const completionTiming = watch("completionTiming");
  const completionDeltaDays = watch("completionDeltaDays");
  const taskStatus = watch("taskStatus");
  const frequency = watch("frequency");
  const interval = watch("interval");
  const isSuggested = watch("isSuggested");

  // When the frequency is removed, reset the interval to 1 if it is < 1
  useEffect(() => {
    if (frequency === Frequency.Once && (!interval || interval < 1)) {
      form.setValue("interval", 1);
    }
  }, [frequency, interval, form]);

  const handleClose = useCallback(() => {
    onClose(form.getValues());
  }, [form, onClose]);

  const handleCancel = useCallback(() => {
    if (onCancelClick) {
      onCancelClick();
      return;
    }
    handleClose();
  }, [handleClose, onCancelClick]);

  const [deleteDocument] = useDeleteDocumentMutation({
    onError: () => {
      alerts.error("Failed to delete document");
    },
  });

  const isCompletionOnly = useCallback(
    (newValues: TaskFormType) => {
      const dirtyFields = form.formState.dirtyFields;
      if (dirtyFields.deadline) {
        // Check if deadline date has changed
        if (!initialValues.deadline || !newValues.deadline) return false;
        const originalDeadline =
          typeof initialValues.deadline === "string"
            ? DateTime.fromISO(initialValues.deadline)
            : DateTime.fromJSDate(initialValues.deadline);
        const updatedDeadline = DateTime.fromJSDate(newValues.deadline);
        const deadlinesAreEqual = originalDeadline.hasSame(updatedDeadline, "day");
        if (!deadlinesAreEqual) return false;
      }
      if (
        // Check for dirty fields other than taskStatus and subtasks
        Object.keys(dirtyFields).some(
          (k) =>
            k !== "taskStatus" &&
            k !== "subtasks" &&
            k !== "deadline" &&
            dirtyFields[k as keyof typeof dirtyFields]
        )
      ) {
        return false;
      }
      // Ensure all subtask changes are completion-state only
      return !dirtyFields.subtasks || dirtyFields.subtasks.every((s) => !s.title && !s.id);
    },
    [form.formState.dirtyFields, initialValues.deadline]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: "min(800px, calc(100vw - 32px))",
          maxWidth: "800px",
        },
      }}
      disableRestoreFocus={disableRestoreFocus}
      noPrimaryAction
      title={
        isLoading ? (
          <Skeleton variant="text" width={200} height={40} />
        ) : (
          <TaskTitleHeader
            control={control}
            initialValues={initialValues}
            virtualEventId={virtualEventId}
            completedOn={completedOn}
            completionTiming={completionTiming}
            completionDeltaDays={completionDeltaDays}
            taskStatus={taskStatus}
            isSuggested={isSuggested}
            onDeleteTask={onDeleteTask}
            autoFocusTitle={autoFocusTitle}
          />
        )
      }
    >
      <FormProvider {...form}>
        {isLoading ? (
          <TaskDialogSkeleton />
        ) : (
          <Stack gap={theme.spacing(2.5)} width="100%" sx={{ pb: showCommentsSection ? 0 : 2 }}>
            <Stack gap={theme.spacing(2)}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextInput
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    variant="description"
                    placeholder="Add task description..."
                    multiline={true}
                    maxRows={10}
                  />
                )}
              />

              <SubtasksControl control={control} />
              <DocumentsListControl
                control={control}
                onDelete={(id: string) => deleteDocument({ variables: { id } })}
              />
              <LinksControl control={control} />
            </Stack>

            <Box
              sx={{
                px: 1.25,
                py: 1.25,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.surfaceSubtle,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="row" gap={theme.spacing(1)} width="100%" flexWrap="wrap">
                <TaskPickerToolbar
                  form={form}
                  onSubmit={handleSubmit((data) =>
                    onSubmit(data, isCompletionOnly(data), !!form.formState.dirtyFields.frequency)
                  )}
                  onCancel={handleCancel}
                  isSubmitting={isSubmitting}
                  cancelButtonText={cancelButtonText}
                  submitButtonText={submitButtonText}
                  cancelButtonDisabled={cancelButtonDisabled}
                  submitButtonHelperText={submitButtonHelperText}
                  submitAlwaysEnabled={submitAlwaysEnabled}
                  showActionButtons={true}
                />
              </Stack>
            </Box>

            {showCommentsSection && (
              <Box sx={{ pb: 1 }}>
                <TaskCommentSection virtualEventId={virtualEventId} comments={comments} />
              </Box>
            )}
          </Stack>
        )}
      </FormProvider>
    </Dialog>
  );
};
